import React from 'react';
import { motion } from 'framer-motion';
import { Users, Sparkles, TrendingUp, Heart, Zap, Award, Quote } from 'lucide-react';
import { testimonials } from '../data/plansData';

const ValueNarrative = () => {
  const values = [
    {
      icon: Users,
      title: 'Conexões Reais',
      description: 'Pessoas reais, trocas genuínas e relacionamentos que transformam carreiras.',
    },
    {
      icon: TrendingUp,
      title: 'Crescimento Contínuo',
      description: 'Aprenda com os melhores e evolua constantemente no ecossistema.',
    },
    {
      icon: Award,
      title: 'Reconhecimento',
      description: 'Sua participação ativa é valorizada com badges, destaques e oportunidades.',
    },
    {
      icon: Zap,
      title: 'Oportunidades',
      description: 'Vagas, parcerias, investimentos e negócios que surgem naturalmente.',
    },
    {
      icon: Heart,
      title: 'Pertencimento',
      description: 'Faça parte de um movimento que transforma o Brasil através da IA.',
    },
    {
      icon: Sparkles,
      title: 'Prosperidade',
      description: 'Mais visibilidade, mais negócios, mais sucesso - resultado natural.',
    },
  ];

  return (
    <section 
      id="value-narrative"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="value-narrative"
    >
      <div className="absolute inset-0 bg-surface-dark">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(0, 255, 0, 0.1) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <motion.div
        className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full bg-neon-green/5 blur-[120px]"
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gold/40 bg-gold/10 text-gold text-sm font-semibold mb-6">
            <Heart className="w-4 h-4" />
            Mais que uma comunidade
          </span>
          <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Você não entra para{' '}
            <span className="text-white/40">consumir conteúdo</span>.
            <br />
            Você entra para{' '}
            <span className="gradient-text-green">construir junto</span>.
          </h2>
          <p className="text-white/70 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            O AI Brasil é um{' '}
            <span className="text-neon-green font-semibold">movimento vivo</span>, onde
            conexões humanas reais impulsionam aprendizado, visibilidade, negócios e{' '}
            <span className="text-gold font-semibold">prosperidade verdadeira</span>.
          </p>
        </motion.div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group p-6 rounded-2xl bg-surface border border-white/10 hover:border-neon-green/30 transition-all"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <motion.div
                className="w-12 h-12 rounded-xl bg-neon-green/10 border border-neon-green/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                whileHover={{ rotate: [0, -10, 10, 0] }}
              >
                <value.icon className="w-6 h-6 text-neon-green" />
              </motion.div>
              <h3 className="font-outfit font-bold text-xl text-white mb-2">
                {value.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8"
        >
          <div className="text-center mb-12">
            <h3 className="font-outfit font-black text-2xl sm:text-3xl text-white mb-2">
              Histórias reais de <span className="gradient-text-green">membros reais</span>
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative p-6 rounded-2xl bg-surface border border-white/10 hover:border-neon-green/30 transition-all"
                whileHover={{ y: -5 }}
              >
                <Quote className="absolute top-4 right-4 w-8 h-8 text-neon-green/20" />
                
                <p className="text-white/80 mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-neon-green/30"
                  />
                  <div>
                    <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                    <p className="text-white/50 text-xs">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValueNarrative;
